import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";

const N = 4;
const COLORS = {
  2: "#1f2937", 4: "#374151", 8: "#0891b2", 16: "#06b6d4",
  32: "#00F0FF", 64: "#22d3ee", 128: "#a3e635", 256: "#39FF14",
  512: "#EFFF04", 1024: "#FF6B00", 2048: "#FF003C",
};

const empty = () => Array.from({ length: N }, () => Array(N).fill(0));

function addTile(b) {
  const cells = [];
  b.forEach((row, r) => row.forEach((v, c) => { if (v === 0) cells.push([r, c]); }));
  if (!cells.length) return b;
  const [r, c] = cells[Math.floor(Math.random() * cells.length)];
  b[r][c] = Math.random() < 0.9 ? 2 : 4;
  return b;
}

function slide(row) {
  let arr = row.filter((v) => v);
  let gained = 0;
  for (let i = 0; i < arr.length - 1; i++) {
    if (arr[i] === arr[i + 1]) { arr[i] *= 2; gained += arr[i]; arr[i + 1] = 0; }
  }
  arr = arr.filter((v) => v);
  while (arr.length < N) arr.push(0);
  return { row: arr, gained };
}

const clone = (b) => b.map((r) => [...r]);
const transpose = (b) => b[0].map((_, c) => b.map((r) => r[c]));
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export function Game2048({ onScore }) {
  const [board, setBoard] = useState(() => addTile(addTile(empty())));
  const [score, setScore] = useState(0);
  const [over, setOver] = useState(false);
  const [won, setWon] = useState(false);
  const reported = useRef(false);

  const reset = () => {
    setBoard(addTile(addTile(empty())));
    setScore(0); setOver(false); setWon(false); reported.current = false;
  };

  const move = useCallback((dir) => {
    setBoard((prev) => {
      let b = clone(prev);
      let gained = 0;
      const rev = (rows) => rows.map((r) => [...r].reverse());
      if (dir === "left" || dir === "right") {
        b = b.map((row) => {
          const src = dir === "right" ? [...row].reverse() : row;
          const { row: nr, gained: g } = slide(src);
          gained += g;
          return dir === "right" ? nr.reverse() : nr;
        });
      } else {
        let t = transpose(b);
        t = t.map((row) => {
          const src = dir === "down" ? [...row].reverse() : row;
          const { row: nr, gained: g } = slide(src);
          gained += g;
          return dir === "down" ? nr.reverse() : nr;
        });
        b = transpose(t);
      }
      if (same(b, prev)) return prev;
      addTile(b);
      setScore((s) => s + gained);
      if (b.flat().includes(2048)) setWon(true);
      // check game over
      const full = !b.flat().includes(0);
      if (full) {
        const movable = ["left", "right", "up", "down"].some(() => false);
        // simpler: check any adjacent equal
        let canMove = false;
        for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
          if (c < N - 1 && b[r][c] === b[r][c + 1]) canMove = true;
          if (r < N - 1 && b[r][c] === b[r + 1][c]) canMove = true;
        }
        if (!canMove) setOver(true);
      }
      return b;
    });
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const map = { ArrowLeft: "left", ArrowRight: "right", ArrowUp: "up", ArrowDown: "down",
        a: "left", d: "right", w: "up", s: "down" };
      const dir = map[e.key];
      if (!dir || over) return;
      e.preventDefault();
      move(dir);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [move, over]);

  useEffect(() => {
    if (over && !reported.current) { reported.current = true; onScore?.(score); }
  }, [over, score, onScore]);

  return (
    <div className="flex flex-col items-center" data-testid="game-2048">
      <div className="flex items-center justify-between w-full max-w-sm mb-4">
        <span className="font-mono text-neon-cyan text-lg" data-testid="2048-score">SCORE: {score}</span>
        <Button onClick={reset} variant="outline" className="border-zinc-700 rounded-none text-white">
          <RotateCcw className="w-4 h-4 mr-1" /> New
        </Button>
      </div>

      <div className="relative bg-zinc-900 p-2 rounded-lg border-2 border-zinc-700" style={{ width: "min(90vw, 360px)" }}>
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${N}, 1fr)` }}>
          {board.flat().map((v, i) => (
            <div key={i} className="aspect-square rounded flex items-center justify-center font-display font-black transition-all"
              style={{
                background: v ? COLORS[v] || "#FF003C" : "#18181b",
                color: v >= 8 ? "#000" : "#fff",
                fontSize: v >= 1024 ? "1.1rem" : "1.6rem",
                boxShadow: v >= 32 ? `0 0 12px ${COLORS[v]}` : "none",
              }}>
              {v || ""}
            </div>
          ))}
        </div>

        {won && !over && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-neon-lime text-black px-3 py-1 text-xs font-bold uppercase rounded">
            2048! Keep going
          </div>
        )}
        {over && (
          <div className="absolute inset-0 bg-black/85 rounded-lg flex flex-col items-center justify-center gap-3" data-testid="2048-gameover">
            <p className="font-display font-black text-2xl text-neon-pink uppercase">Game Over</p>
            <p className="font-mono text-white">Score: {score}</p>
            <Button onClick={reset} className="bg-neon-cyan text-black rounded-none font-bold uppercase">Try Again</Button>
          </div>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono mt-3">Arrow keys / WASD to slide tiles</p>
    </div>
  );
}
